import $ from 'jquery';
import 'select2';

/**
 * Temporarily remove data-cascade-* attributes before Select2 init
 * to prevent Select2 from misinterpreting them (e.g. data-cascade-child-2
 * causes "cannot use 'in' operator" error).
 * Also clears jQuery's internal data cache for these keys.
 */
function stripCascadeData($el) {
  const saved = {};
  const el = $el[0];
  if (!el || !el.attributes) return saved;

  const attrs = el.attributes;
  const toRemove = [];

  let hasCascade = false;
  for (let i = 0; i < attrs.length; i++) {
    const name = attrs[i].name;
    if (
      name.startsWith('data-cascade-') ||
      name.startsWith('data-add-cascade-') ||
      name.startsWith('data-value-key') ||
      name.startsWith('data-text-key') ||
      name.startsWith('data-add-value-key') ||
      name.startsWith('data-add-text-key')
    ) {
      saved[name] = attrs[i].value;
      toRemove.push(name);
      hasCascade = true;
    }
  }

  if (!hasCascade) return null; // Signal that no stripping was done

  toRemove.forEach((name) => {
    $el.removeAttr(name);
    const key = name
      .replace(/^data-/, '')
      .replace(/-([a-z0-9])/g, (_, letter) => letter.toUpperCase());
    $el.removeData(key);
  });
  return saved;
}

function restoreCascadeData($el, saved) {
  if (!saved || !$el.length) return;
  Object.entries(saved).forEach(([name, value]) => {
    $el.attr(name, value);
  });
}

/**
 * Robust Global Select2 Initialization
 * Defines initialization logic for `.select2` and premium variants.
 */
export function initGlobalSelect2(container = document) {
  const $container = $(container);
  const isModal = $container.hasClass('modal') || $container.closest('.modal').length > 0;

  // Standard Select2
  $container.find('.select2').each(function () {
    if ($(this).hasClass('select2-hidden-accessible')) return;

    // On page load (container = document), skip elements inside hidden modals
    // They will be initialized when modal is shown via shown.bs.modal event
    if (!isModal && $(this).closest('.modal').length) return;

    // IMPORTANT: Strip cascade attributes FIRST, before any .data() call.
    // jQuery's .data() lazily caches ALL data-* attributes on first access.
    // If we call .data('placeholder') first, it caches data-cascade-child-2 too,
    // which causes Select2's _convertData to crash.
    const saved = stripCascadeData($(this));

    // Now safe to read placeholder (jQuery will re-read from clean DOM)
    const placeholder = $(this).attr('data-placeholder') || '-- Pilih --';

    try {
      $(this).select2({
        theme: 'bootstrap-5',
        width: '100%',
        placeholder: placeholder,
        allowClear: true,
        dropdownParent: $(this).closest('.modal').length
          ? $(this).closest('.modal')
          : $(document.body),
      });
    } catch (err) {
      console.warn(
        '[Select2] Init error on element:',
        $(this).attr('id') || $(this).attr('name'),
        err.message
      );
    }

    if (saved) restoreCascadeData($(this), saved);
  });

  // Premium Styled Select2 for Inspeksi/Master Data
  $container.find('.select2-inspeksi').each(function () {
    if ($(this).hasClass('select2-hidden-accessible')) return;

    // On page load (container = document), skip elements inside hidden modals
    if (!isModal && $(this).closest('.modal').length) return;

    // Strip FIRST, then read placeholder
    const saved = stripCascadeData($(this));
    const placeholder = $(this).attr('data-placeholder') || '-- Pilih --';

    try {
      $(this).select2({
        theme: 'bootstrap-5',
        width: '100%',
        placeholder: placeholder,
        allowClear: true,
        dropdownParent: $(this).closest('.modal').length
          ? $(this).closest('.modal')
          : $(document.body),
      });
    } catch (err) {
      console.warn(
        '[Select2] Init error on element:',
        $(this).attr('id') || $(this).attr('name'),
        err.message
      );
    }

    // Restore cascade data attributes
    if (saved) restoreCascadeData($(this), saved);

    // Ensure hidden original
    $(this).css('display', 'none').attr('aria-hidden', 'true');
  });
}
